import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { format, addDays, parseISO } from "date-fns";
import {
  CaretLeft,
  CaretRight,
  Check,
  Trash,
  Plus,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AppHeader } from "@/components/AppHeader";
import { DateField } from "@/components/DateTimeField";
import { api, formatApiErrorDetail } from "@/lib/api";

const iso = (d) => format(d, "yyyy-MM-dd");

export default function Tasks() {
  const [date, setDate] = useState(iso(new Date()));
  const [tasks, setTasks] = useState([]);
  const [crew, setCrew] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [draft, setDraft] = useState({
    title: "",
    assignee: "none",
    job_id: "none",
  });

  const load = useCallback(async () => {
    const [t, c, j] = await Promise.all([
      api.get("/tasks", { params: { date, carry: true } }),
      api.get("/crew-list"),
      api.get("/jobs"),
    ]);
    setTasks(t.data);
    setCrew(c.data);
    setJobs(j.data);
  }, [date]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async (e) => {
    e.preventDefault();
    const job = jobs.find((x) => x.id === draft.job_id);
    try {
      await api.post("/tasks", {
        title: draft.title,
        date,
        assignee: draft.assignee === "none" ? "" : draft.assignee,
        job_id: draft.job_id === "none" ? null : draft.job_id,
        job_code: job?.code || "",
      });
      setDraft({ title: "", assignee: draft.assignee, job_id: "none" });
      toast.success("Task added");
      load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not add task",
      );
    }
  };

  const toggle = async (t) => {
    await api.patch(`/tasks/${t.id}/done`, { done: !t.done });
    load();
  };

  const remove = async (t) => {
    await api.delete(`/tasks/${t.id}`);
    toast.success("Task removed");
    load();
  };

  const carried = tasks.filter((t) => t.date !== date && !t.done);
  const todays = tasks.filter((t) => t.date === date);
  const done = todays.filter((t) => t.done).length;

  const row = (t, isCarried) => (
    <div
      key={t.id}
      data-testid={`task-${t.id}`}
      className={`flex items-center gap-3 rounded-lg border p-3 transition-colors duration-150 ${
        t.done
          ? "border-emerald-500/30 bg-zinc-900/60"
          : "border-zinc-800 bg-zinc-900"
      }`}
    >
      <button
        data-testid={`task-toggle-${t.id}`}
        onClick={() => toggle(t)}
        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded border transition-colors duration-150 ${
          t.done
            ? "border-emerald-500 bg-emerald-500 text-black"
            : "border-zinc-600 hover:border-yellow-500"
        }`}
      >
        {t.done && <Check size={15} weight="bold" />}
      </button>
      <div className="min-w-0 flex-1">
        <div
          className={`truncate text-sm font-semibold ${t.done ? "text-zinc-500 line-through" : ""}`}
        >
          {t.title}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-500">
          {t.assignee ? (
            <span className="text-yellow-400">{t.assignee}</span>
          ) : (
            <span>Whole shop</span>
          )}
          {t.job_code && <span className="font-mono-data">{t.job_code}</span>}
          {isCarried && (
            <span className="text-red-400">carried from {t.date}</span>
          )}
        </div>
      </div>
      <button
        data-testid={`task-delete-${t.id}`}
        onClick={() => remove(t)}
        className="shrink-0 text-zinc-600 transition-colors duration-150 hover:text-red-400"
      >
        <Trash size={16} weight="bold" />
      </button>
    </div>
  );

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Daily tasks" />
      <main className="relative z-10 pb-28 pt-6">
        <div
          className="rounded-lg border border-zinc-800 bg-zinc-900 p-5"
          data-testid="tasks-header-card"
        >
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-zinc-500">
                Shop floor
              </div>
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
                Daily Tasks
              </h1>
              <p
                className="mt-1 text-sm text-zinc-500"
                data-testid="tasks-summary"
              >
                {date} · {done} of {todays.length} done
                {carried.length > 0 ? ` · ${carried.length} carried over` : ""}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                data-testid="tasks-prev"
                variant="outline"
                onClick={() => setDate(iso(addDays(parseISO(date), -1)))}
                className="h-11 border-zinc-800 bg-zinc-900 px-3"
              >
                <CaretLeft size={16} weight="bold" />
              </Button>
              <div className="w-56">
                <DateField
                  testId="tasks-date"
                  value={date}
                  onChange={(v) => v && setDate(v)}
                />
              </div>
              <Button
                data-testid="tasks-next"
                variant="outline"
                onClick={() => setDate(iso(addDays(parseISO(date), 1)))}
                className="h-11 border-zinc-800 bg-zinc-900 px-3"
              >
                <CaretRight size={16} weight="bold" />
              </Button>
            </div>
          </div>
        </div>

        <form
          onSubmit={add}
          data-testid="task-form"
          className="mt-6 grid gap-3 rounded-lg border border-zinc-800 bg-zinc-900 p-4 lg:grid-cols-[2fr_1fr_1fr_auto]"
        >
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-zinc-500">
              Task
            </Label>
            <Input
              data-testid="task-title-input"
              required
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              placeholder="Polish edges on SLB-1092"
              className="h-12 border-zinc-800 bg-zinc-950"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-zinc-500">
              Assign to
            </Label>
            <Select
              value={draft.assignee}
              onValueChange={(v) => setDraft({ ...draft, assignee: v })}
            >
              <SelectTrigger
                data-testid="task-assignee-select"
                className="h-12 border-zinc-800 bg-zinc-950"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-zinc-800 bg-zinc-900">
                <SelectItem value="none">Whole shop</SelectItem>
                {crew.map((u) => (
                  <SelectItem key={u.id} value={u.name || u.email}>
                    {u.name || u.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-zinc-500">
              Link job
            </Label>
            <Select
              value={draft.job_id}
              onValueChange={(v) => setDraft({ ...draft, job_id: v })}
            >
              <SelectTrigger
                data-testid="task-job-select"
                className="h-12 border-zinc-800 bg-zinc-950"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-zinc-800 bg-zinc-900">
                <SelectItem value="none">No job</SelectItem>
                {jobs.map((j) => (
                  <SelectItem key={j.id} value={j.id}>
                    {j.code} — {j.project_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button
              data-testid="add-task-button"
              type="submit"
              className="h-12 w-full bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400 lg:w-auto lg:px-6"
            >
              <Plus size={16} weight="bold" className="mr-2" /> Add
            </Button>
          </div>
        </form>

        {carried.length > 0 && (
          <div className="mt-8">
            <div className="text-xs uppercase tracking-widest text-zinc-500">
              Carried Over
            </div>
            <div className="mt-3 space-y-2" data-testid="carried-tasks">
              {carried.map((t) => row(t, true))}
            </div>
          </div>
        )}

        <div className="mt-8">
          <div className="text-xs uppercase tracking-widest text-zinc-500">
            Today's List
          </div>
          <div className="mt-3 space-y-2" data-testid="task-list">
            {todays.length === 0 && (
              <p className="rounded-lg border border-dashed border-zinc-800 p-8 text-center text-sm text-zinc-500">
                No tasks for this day yet.
              </p>
            )}
            {todays.map((t) => row(t, false))}
          </div>
        </div>
      </main>
    </div>
  );
}
